/* *****************************************************************************
 * Adapted from dijkstra.js Feb 21st 2024 to add maximum depth.
 * This was licensed under the MIT license, which permits modification without distribution
 * So long as one keeps the license information in tact:
 *
 * Created 2008-08-19.
 *
 * Dijkstra path-finding functions. Adapted from the Dijkstar Python project.
 *
 * Copyright (C) 2008
 *   Wyatt Baldwin <self@wyattbaldwin.com>
 *   All rights reserved
 *
 * Licensed under the MIT license.
 *
 *   http://www.opensource.org/licenses/mit-license.php
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 **************************************************************************** */

const ROUTING_DEPTH_RETRY = 30;
const ROUTING_DEPTH_MAX = 100;
/**
 * A very naive priority queue implementation.
 */
// eslint-disable-next-line max-classes-per-file
class PriorityQueue {
  private queue: Array<any>;
  private sorter: any;
  constructor(opts: { sorter?: any } = {}) {
    opts = opts || {};
    this.queue = [];
    this.sorter = opts.sorter || this.default_sorter;
  }

  default_sorter(a, b) {
    return a.cost - b.cost;
  }

  /**
   * Add a new item to the queue and ensure the highest priority element
   * is at the front of the queue.
   */
  push(value, cost, depth) {
    const item = { value, cost, depth };
    this.queue.push(item);
    this.queue.sort(this.sorter);
  }

  /**
   * Return the highest priority element in the queue.
   */
  pop() {
    return this.queue.shift();
  }

  empty() {
    return this.queue.length === 0;
  }
}

export class Dijkstra {
  private graph: Map<string, Map<string, number>>;
  constructor(graph) {
    this.graph = graph;
  }

  single_source_shortest_paths(
    s: string,
    d: string,
    depth_limit?: number,
  ): Map<string, string> {
    // Predecessor map for each node that has been encountered.
    // node ID => predecessor node ID
    const predecessors = new Map<string, string>();
    depth_limit = depth_limit || Number('inf');

    // Costs of shortest paths from s to all nodes encountered.
    // node ID => cost
    const costs = {};
    costs[s] = 0;

    // Costs of shortest paths from s to all nodes encountered; differs from
    // `costs` in that it provides easy access to the node that currently has
    // the known shortest path from s.
    // XXX: Do we actually need both `costs` and `open`?
    const open = new PriorityQueue();
    open.push(s, 0, 0);

    while (!open.empty()) {
      // In the nodes remaining in graph that have a known cost from s,
      // find the node, u, that currently has the shortest path from s.
      const closest = open.pop();
      const u = closest.value;
      const cost_of_s_to_u = closest.cost;
      if (cost_of_s_to_u !== costs[u]) {
        console.log('Cost of s to u != costs[u]', u, cost_of_s_to_u, costs[u]);
      }

      // Shortcut execution if depth limit is reached
      if (closest.depth > depth_limit) {
        // eslint-disable-next-line no-continue
        continue;
      }

      // Get nodes adjacent to u...
      const adjacent_nodes = this.graph[u] || {};

      // ...and explore the edges that connect u to those nodes, updating
      // the cost of the shortest paths to any or all of those nodes as
      // necessary. v is the node across the current edge from u.
      const adjacent_node_keys = Object.keys(adjacent_nodes);
      for (let i = 0; i < adjacent_node_keys.length; i++) {
        const v = adjacent_node_keys[i];
        // Get the cost of the edge running from u to v.
        const cost_of_e = adjacent_nodes[v];

        // Cost of s to u plus the cost of u to v across e--this is *a*
        // cost from s to v that may or may not be less than the current
        // known cost to v.
        const cost_of_s_to_u_plus_cost_of_e = cost_of_s_to_u + cost_of_e;

        // If we haven't visited v yet OR if the current known cost from s to
        // v is greater than the new cost we just found (cost of s to u plus
        // cost of u to v across e), update v's cost in the cost list and
        // update v's predecessor in the predecessor list (it's now u).
        const cost_of_s_to_v = costs[v];
        const first_visit = cost_of_s_to_v === undefined;
        if (first_visit || cost_of_s_to_v > cost_of_s_to_u_plus_cost_of_e) {
          costs[v] = cost_of_s_to_u_plus_cost_of_e;
          open.push(v, cost_of_s_to_u_plus_cost_of_e, closest.depth + 1);
          predecessors[v] = u;
        }
      }
    }

    if (d !== undefined && costs[d] === undefined) {
      const msg = ['Could not find a path from ', s, ' to ', d, '.'].join('');
      throw new Error(msg);
    }

    return predecessors;
  }

  extract_shortest_path_from_predecessor_list(
    predecessors: Map<string, string>,
    d: string,
  ) {
    const nodes = [];
    let u = d;
    while (u) {
      nodes.push(u);
      u = predecessors[u];
    }
    nodes.reverse();
    return nodes;
  }

  find_path(s: string, d: string) {
    let predecessors;
    try {
      predecessors = this.single_source_shortest_paths(
        s,
        d,
        ROUTING_DEPTH_RETRY,
      );
    } catch (e) {
      console.debug('Failed to find path in 30 steps, trying with 100');
      predecessors = this.single_source_shortest_paths(s, d, ROUTING_DEPTH_MAX);
    }
    return this.extract_shortest_path_from_predecessor_list(predecessors, d);
  }
}
